import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../utils/translations';
import supabase from '../../lib/supabase';
import Button from "../Common/Button"

const { FiMail, FiLock, FiEye, FiEyeOff } = FiIcons;

const LoginForm = ({ isRegister, setIsRegister }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { login, register } = useAuth();
  const t = useTranslation('fr'); // Default to French

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      if (isRegister) {
        if (formData.password !== formData.confirmPassword) {
          throw new Error(t('auth.passwordsDoNotMatch', 'Les mots de passe ne correspondent pas'));
        }

        // Register with Supabase
        const { data, error: signUpError } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
        });

        if (signUpError) throw signUpError;

        // Create user profile
        if (data?.user) {
          const { error: profileError } = await supabase
            .from('user_profiles_a1b2c3d4e5')
            .insert([
              {
                id: data.user.id,
                full_name: formData.fullName,
                role: 'Team Member', // Default role for new registrations
                language: 'fr'
              }
            ]);

          if (profileError) throw profileError;
          
          // Show success message instead of auto-login
          setSuccess(t('auth.registerSuccess', 'Merci de vous être inscrit ! Veuillez vérifier votre boîte de réception et cliquer sur le lien pour vérifier votre courriel.'));
          
          // We'll not auto-login until they verify email
          // await register({
          //   fullName: formData.fullName,
          //   email: formData.email,
          //   password: formData.password,
          //   language: 'fr',
          //   role: 'Team Member' // Default role for self-registration
          // });
        }
      } else {
        // Login with Supabase
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: formData.email,
          password: formData.password,
        });

        if (signInError) throw signInError;
        await login(formData.email, formData.password);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError('');
    setResetSuccess('');
    
    try {
      // Reset password with Supabase
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        resetEmail,
        {
          redirectTo: window.location.origin // Adjust as needed for your app's redirect URL
        }
      );

      if (resetError) throw resetError;

      setResetSuccess(t('auth.resetEmailSent', 'Vérifiez votre boîte de réception pour un lien de réinitialisation de mot de passe.'));
      setResetEmail('');
    } catch (err) {
      setResetError(err.message);
    } finally {
      setResetLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleResetEmailChange = (e) => {
    setResetEmail(e.target.value);
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    setShowResetModal(true);
    setResetError('');
    setResetSuccess('');
    setResetEmail(formData.email); // Pre-fill with current email if available
  };

  const handleCloseResetModal = () => {
    setShowResetModal(false);
    setResetError('');
    setResetSuccess('');
    setResetEmail('');
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 lg:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full space-y-8"
        >
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary-500 rounded-xl flex items-center justify-center mb-4">
              <SafeIcon icon={FiMail} className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">
              {isRegister ? t('auth.register', 'S\'inscrire') : t('auth.login', 'Connexion')}
            </h2>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              {isRegister && (
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('auth.fullName', 'Nom complet')}
                  </label>
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    required
                    value={formData.fullName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.email', 'Courriel')}
                </label>
                <div className="relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <SafeIcon icon={FiMail} className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.password', 'Mot de passe')}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <SafeIcon icon={FiLock} className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                  <Button
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5"
                    icon={showPassword ? FiEyeOff : FiEye}
                    iconClassName="w-5 h-5 text-gray-400 hover:text-gray-600"
                  />
                </div>
              </div>

              {isRegister && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('auth.confirmPassword', 'Confirmer le mot de passe')}
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <SafeIcon icon={FiLock} className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />                
                    <Button
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-2.5"
                      icon={showPassword ? FiEyeOff : FiEye}
                      iconClassName="w-5 h-5 text-gray-400 hover:text-gray-600"
                    />
                  </div>
                </div>
              )}
            </div>

            {!isRegister && (
              <div className="text-center">
                <Button
                  onClick={handleForgotPassword}
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium"
                >
                  {t('auth.forgotPasswordLink', 'Mot de passe oublié ?')}
                </Button>
              </div>
            )}

            {error && (
              <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-lg">
                {error}
              </div>
            )}

            {success && (
              <div className="text-green-600 text-sm text-center bg-green-50 p-3 rounded-lg">
                {success}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || success}
              appendClassName="w-full justify-center"
              loading={loading}
            >
              {isRegister ? t('auth.register', 'S\'inscrire') : t('auth.signIn', 'Se connecter')}
            </Button>

            <div className="text-center">
              <Button
                onClick={() => {
                  setIsRegister(!isRegister);
                  setError('');
                  setSuccess('');
                }}
                className="text-primary-500 hover:text-primary-600 text-sm font-medium" 
              >
                {isRegister ? t('auth.alreadyHaveAccount', 'Vous avez déjà un compte ? Connectez-vous') : t('auth.dontHaveAccount', 'Pas de compte ? Inscrivez-vous')}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>

      {/* Reset Password Modal */}
      {showResetModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseResetModal}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-lg p-6 max-w-md w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {t('auth.forgotPassword', 'Mot de passe oublié')}
              </h3>
              <p className="text-gray-600">
                {t('auth.resetDescription', 'Entrez votre adresse courriel pour recevoir un lien de réinitialisation.')}
              </p>
            </div>

            <form className="space-y-4" onSubmit={handleResetSubmit}>
              <div>
                <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.email', 'Courriel')}
                </label>
                <div className="relative">
                  <input
                    id="resetEmail"
                    name="resetEmail"
                    type="email"
                    required
                    value={resetEmail}
                    onChange={handleResetEmailChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <SafeIcon icon={FiMail} className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                </div>
              </div>

              {resetError && (
                <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-lg">
                  {resetError}
                </div>
              )}

              {resetSuccess && (
                <div className="text-green-600 text-sm text-center bg-green-50 p-3 rounded-lg">
                  {resetSuccess}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={handleCloseResetModal}
                  disabled={resetLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                >
                  {t('auth.cancel', 'Annuler')}
                </Button>
                <Button
                  type="submit"
                  disabled={resetLoading || resetSuccess}
                  loading={resetLoading}
                  appendClassName="px-4 py-2"
                >
                  {t('auth.sendResetLink', 'Envoyer le lien')}
                </Button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default LoginForm;